import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse } from '../utils/api';

interface Comment {
  id: number;
  cod_comentario: string;
  comentario: string;
  familia: string | null;
  articulo: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export const Extras: React.FC = () => {
  const { token } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  
  // Filtros adicionales
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFamily, setSelectedFamily] = useState<string>('');
  const [selectedArticle, setSelectedArticle] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Función para cargar comentarios
  const loadComments = async (showToast: boolean = true) => {
    if (!token) {
      setComments([]);
      return;
    }

    setLoadingComments(true);
    try {
      const params = new URLSearchParams();
      if (selectedFamily) {
        params.append('familia', selectedFamily);
      }
      if (selectedArticle) {
        params.append('articulo', selectedArticle);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('page', '1');
      params.append('limit', '1000');

      const route = `comments/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        if (showToast) {
          toast.error('Error de conexión al cargar comentarios');
        }
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar comentarios';
        throw new Error(errorMessage);
      }

      setComments(json.data?.comments || []);
      
      if (showToast) {
        const total = json.data?.total || 0;
        if (total === 0) {
          toast.info('No se encontraron comentarios');
        } else {
          toast.success(`${total} comentario(s) encontrado(s)`);
        }
      }
    } catch (err: any) {
      console.error('Error cargando comentarios:', err);
      if (showToast) {
        toast.error(err.message || 'Error al cargar comentarios');
      }
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  // Cargar comentarios cuando cambian los filtros
  useEffect(() => {
    loadComments();
  }, [token, selectedFamily, selectedArticle, searchTerm]);

  const handleSync = async () => {
    if (!token) {
      toast.error('No hay token de autenticación');
      return;
    }
    
    setSyncing(true);
    try {
      console.log('🔄 Iniciando sincronización de comentarios...');
      
      const res = await apiFetch(
        'comments/sync',
        {
          method: 'POST',
          body: JSON.stringify({})
        },
        token
      );
      
      console.log('📡 Respuesta HTTP recibida:', {
        status: res.status,
        ok: res.ok,
        statusText: res.statusText
      });
      
      if (isNetworkErrorResponse(res)) {
        console.error('❌ Error de red al sincronizar');
        toast.error('Error de conexión al sincronizar');
        return;
      }
      
      // Leer el texto crudo antes de parsear para debugging
      const text = await res.text();
      console.log('📄 Respuesta cruda (primeros 500 caracteres):', text.substring(0, 500));
      
      // Intentar parsear JSON
      let json;
      try {
        json = JSON.parse(text);
        console.log('✅ JSON parseado correctamente');
      } catch (parseError: any) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('📄 Texto completo recibido:', text);
        toast.error('Error: La respuesta del servidor no es JSON válido. Revisa la consola para más detalles.');
        return;
      }
      
      // Log completo de la respuesta
      console.log('📦 Respuesta completa de sincronización:', {
        status: res.status,
        ok: res.ok,
        json: json,
        data: json.data,
        success: json.success,
        error: json.error
      });
      
      if (json.error || !json.success) {
        let errorMessage = 'Error al sincronizar comentarios';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        
        console.error('❌ Error en sincronización:', {
          error: json.error,
          message: json.message,
          data: json.data,
          errorMessage
        });
        
        toast.error(errorMessage);
      } else {
        const data = json.data || {};
        const loginSuccess = data.loginSuccess ?? false;
        const exportSuccess = data.exportSuccess ?? false;
        
        console.log('✅ Respuesta exitosa:', {
          loginSuccess,
          exportSuccess,
          hasExportData: !!data.export_data,
          exportError: data.exportError
        });
        
        if (!loginSuccess) {
          toast.error('Error en la autenticación con Hioffice');
        } else if (!exportSuccess) {
          const exportError = data.exportError || 'Error desconocido en exportación';
          toast.error(`Error en exportación: ${exportError}`);
        } else {
          const synced = data.synced || 0;
          const updated = data.updated || 0;
          const total = data.total || 0;
          
          let message = `Sincronización exitosa: ${synced} nuevo(s), ${updated} actualizado(s), ${total} total`;
          
          toast.success(message, {
            duration: Infinity,
            action: {
              label: 'Enterado',
              onClick: () => {}
            }
          });
          
          // Recargar comentarios sin mostrar toast adicional
          loadComments(false);
        }
      }
    } catch (err: any) {
      console.error('❌ Error sincronizando comentarios:', err);
      console.error('Stack trace:', err.stack);
      toast.error(err.message || 'Error al sincronizar comentarios');
    } finally {
      setSyncing(false);
    }
  };

  // Obtener familias y artículos únicos de los comentarios
  const getUniqueFamilies = (): string[] => {
    const families = new Set<string>();
    comments.forEach(comment => {
      if (comment.familia) {
        families.add(comment.familia);
      }
    });
    return Array.from(families).sort();
  };

  const getUniqueArticles = (): string[] => {
    const articles = new Set<string>();
    comments.forEach(comment => {
      if (comment.articulo) {
        articles.add(comment.articulo);
      }
    });
    return Array.from(articles).sort();
  };

  // Aplicar filtros a los comentarios
  const getFilteredComments = (): Comment[] => {
    return comments.filter(comment => {
      // Filtro de búsqueda por código o comentario
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesCode = comment.cod_comentario.toLowerCase().includes(searchLower);
        const matchesComment = comment.comentario.toLowerCase().includes(searchLower);
        if (!matchesCode && !matchesComment) {
          return false;
        }
      }

      // Filtro por familia
      if (selectedFamily && comment.familia !== selectedFamily) {
        return false;
      }

      // Filtro por artículo
      if (selectedArticle && comment.articulo !== selectedArticle) {
        return false;
      }

      return true;
    });
  };

  const filteredComments = getFilteredComments();

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedFamily('');
    setSelectedArticle('');
  };

  const hasActiveFilters = searchTerm || selectedFamily || selectedArticle;

  return (
    <DashboardLayout
      title="Gestión de Extras"
      subtitle="Visualiza y gestiona los comentarios de exportación"
      showBackButton={true}
      backPath="/management"
    >
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filtros principales */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Los comentarios son globales y se sincronizan a nivel de cliente. 
                  La sincronización utilizará las credenciales del cliente asociado a tu cuenta.
                </p>
              </div>

              {/* Filtros adicionales */}
              {comments.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-2"
                    >
                      {showFilters ? '▼' : '▶'} Filtros adicionales
                    </button>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </div>

                  {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                      {/* Búsqueda por código o comentario */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buscar comentario
                        </label>
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Código o texto del comentario..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Filtro por familia */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Familia
                        </label>
                        <select
                          value={selectedFamily}
                          onChange={(e) => setSelectedFamily(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Todas las familias</option>
                          {getUniqueFamilies().map((family) => (
                            <option key={family} value={family}>
                              {family}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Filtro por artículo */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Artículo
                        </label>
                        <select
                          value={selectedArticle}
                          onChange={(e) => setSelectedArticle(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Todos los artículos</option>
                          {getUniqueArticles().map((article) => (
                            <option key={article} value={article}>
                              {article}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Indicador de filtros activos */}
                  {hasActiveFilters && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="text-xs text-gray-600">Filtros activos:</span>
                      {searchTerm && (
                        <Badge variant="info" className="text-xs">
                          Buscar: "{searchTerm}"
                        </Badge>
                      )}
                      {selectedFamily && (
                        <Badge variant="info" className="text-xs">
                          Familia: {selectedFamily}
                        </Badge>
                      )}
                      {selectedArticle && (
                        <Badge variant="info" className="text-xs">
                          Artículo: {selectedArticle}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end mt-4">
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  {syncing ? 'Sincronizando...' : 'Sincronizar desde Hioffice'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Mensaje de error */}
          {error && (
            <Alert variant="error" className="mb-6">
              {error}
            </Alert>
          )}

          {/* Lista de comentarios */}
          {loadingComments ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">Cargando comentarios...</p>
              </CardContent>
            </Card>
          ) : comments.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">No se encontraron comentarios.</p>
                <p className="text-sm text-gray-400 mt-2">
                  Sincroniza desde Hioffice para importar comentarios.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Comentarios {hasActiveFilters ? `(${filteredComments.length} de ${comments.length})` : `(${comments.length})`}
                </h2>
              </div>
              {hasActiveFilters && filteredComments.length === 0 && (
                <Alert variant="warning" className="mb-4">
                  No se encontraron comentarios que coincidan con los filtros aplicados.
                </Alert>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredComments.map((comment) => (
                  <Card key={comment.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="info" className="text-xs">
                            {comment.cod_comentario}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          {comment.comentario}
                        </h3>
                      </div>

                      <div className="space-y-2 mb-4">
                        {comment.familia && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Familia:</span>
                            <Badge variant="success">
                              {comment.familia}
                            </Badge>
                          </div>
                        )}
                        {comment.articulo && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Artículo:</span>
                            <span className="text-sm text-gray-800">{comment.articulo}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Creado: {new Date(comment.created_at).toLocaleDateString('es-MX')}</span>
                          {comment.updated_at !== comment.created_at && (
                            <span>Actualizado: {new Date(comment.updated_at).toLocaleDateString('es-MX')}</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

